import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackPurchaseDeleted } from '../services/analytics';
import { Toast, Modal, DetailRow, PageHeader, SearchBar, Pagination, DeleteConfirmModal, BtnSecondary, ActionButtons, LoadingSpinner, EmptyState, formatDate } from './v2-utils';
import '../styles/v2.css';

const COLORS = ['linear-gradient(135deg,#f87171,#ef4444)','linear-gradient(135deg,#fbbf24,#d97706)','linear-gradient(135deg,#60a5fa,#3b82f6)','linear-gradient(135deg,#a78bfa,#8b5cf6)','linear-gradient(135deg,#34d399,#10b981)','linear-gradient(135deg,#f472b6,#ec4899)','linear-gradient(135deg,#fb923c,#ea580c)'];
const PAGE_SIZES = [6, 12, 18];

function getInitials(name) {
  if (!name) return '??';
  return name.split(/[\s\-()\[\]]+/).filter(Boolean).map(w=>w[0]).join('').toUpperCase().slice(0,2);
}
function formatProductName(p) {
  let s = p.product_name||'';
  if (p.variety) s+=` - ${p.variety}`;
  if (p.brand)   s+=` (${p.brand})`;
  if (p.size)    s+=` [${p.size}]`;
  return s;
}

export default function ViewPurchasesV2() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(0);
  const [pageSize, setPageSize]   = useState(6);
  const [selected, setSelected]   = useState(null);
  const [toDelete, setToDelete]   = useState(null);
  const [toast, setToast]         = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axiosInstance.get('/purchases').then(r => setPurchases(r.data||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  useEffect(() => { setPage(0); }, [search]);

  const filtered = purchases.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${p.product_name} ${p.brand} ${p.variety} ${p.size} ${p.supplier_name||''}`.toLowerCase().includes(q);
  });
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated  = filtered.slice(page*pageSize, page*pageSize+pageSize);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/purchases/${toDelete.purchase_id}`);
      trackPurchaseDeleted(toDelete.purchase_id);
      setPurchases(prev => prev.filter(p => p.purchase_id !== toDelete.purchase_id));
      setToast({ message: 'Purchase deleted.', severity: 'success' });
    } catch { setToast({ message: 'Failed to delete.', severity: 'error' }); }
    finally { setToDelete(null); }
  };

  if (loading) return <LoadingSpinner message="Loading purchases…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader title="Purchases" subtitle={`${filtered.length} record${filtered.length!==1?'s':''}`}
        action={<button onClick={() => navigate('/new/dashboard/purchases/add')} className="v2-btn-submit" style={{ width:'auto', padding:'10px 20px', fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
          <svg style={{ width:15,height:15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>Add Purchase</button>} />

      <SearchBar value={search} onChange={setSearch} placeholder="Search by product, brand, variety, supplier…" />

      {paginated.length === 0 ? <EmptyState message={search ? 'No purchases match.' : 'No purchases recorded yet.'} /> : isDesktop ? (
        <div className="v2-card" style={{ overflow: 'hidden' }}>
          <table className="v2-table" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #f1f5f1' }}>
                {['Product','Supplier','Unit','Price (K)','Qty','Discount (K)','Date','Actions'].map(h => (
                  <th key={h} style={{ padding:'12px 20px', textAlign:h==='Product'?'left':'center', fontSize:10.5, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((p, i) => (
                <tr key={p.purchase_id} style={{ borderBottom:i<paginated.length-1?'1px solid #f8faf8':'none' }}>
                  <td style={{ padding:'13px 20px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:11 }}>
                      <div style={{ width:32, height:32, borderRadius:9, background:COLORS[i%COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <span style={{ color:'white', fontSize:11, fontWeight:900 }}>{getInitials(p.product_name)}</span>
                      </div>
                      <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#1e293b', maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{formatProductName(p)}</p>
                    </div>
                  </td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#475569' }}>{p.supplier_name||'—'}</td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#475569' }}>{p.unit_type||'—'}</td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, fontWeight:700, color:'#1e293b' }}>{p.order_price}</td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#475569' }}>{p.quantity}</td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:p.discount>0?'#ef4444':'#94a3b8' }}>{p.discount>0?`−${p.discount}`:'—'}</td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#64748b', whiteSpace:'nowrap' }}>{formatDate(p.purchase_date)}</td>
                  <td style={{ padding:'13px 20px' }}>
                    <ActionButtons onEdit={() => navigate(`/new/dashboard/purchases/edit/${p.purchase_id}`)} onInfo={() => setSelected(p)} onDelete={() => setToDelete(p)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {paginated.map((p, i) => (
            <div key={p.purchase_id} className="v2-card" style={{ padding:18, overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:14 }}>
                <div style={{ width:40, height:40, borderRadius:12, background:COLORS[i%COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ color:'white', fontSize:12, fontWeight:900 }}>{getInitials(p.product_name)}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{formatProductName(p)}</p>
                  <p style={{ margin:'2px 0 0', fontSize:12, color:'#64748b' }}>{p.supplier_name || '—'}</p>
                  <p style={{ margin:'2px 0 0', fontSize:12, color:'#94a3b8' }}>{formatDate(p.purchase_date)}</p>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, paddingTop:14, borderTop:'1px solid #f1f5f1', marginBottom:14 }}>
                {[{label:'Price (K)',value:p.order_price},{label:'Qty',value:p.quantity},{label:'Unit',value:p.unit_type||'—'},{label:'Discount',value:p.discount>0?`K ${p.discount}`:'—'}].map(item=>(
                  <div key={item.label} style={{ textAlign:'center' }}>
                    <p style={{ margin:0, fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>{item.label}</p>
                    <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#334155' }}>{item.value}</p>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => navigate(`/new/dashboard/purchases/edit/${p.purchase_id}`)} style={{ flex:1, padding:'8px 0', borderRadius:10, border:'1.5px solid #e2e8f0', background:'white', color:'#475569', fontSize:12, fontWeight:600, cursor:'pointer' }}>Edit</button>
                <button onClick={() => setSelected(p)} style={{ flex:1, padding:'8px 0', borderRadius:10, border:'1.5px solid #e2e8f0', background:'white', color:'#475569', fontSize:12, fontWeight:600, cursor:'pointer' }}>Details</button>
                <button onClick={() => setToDelete(p)} style={{ flex:1, padding:'8px 0', borderRadius:10, border:'1.5px solid #fecaca', background:'#fef2f2', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={filtered.length}
        onPageChange={setPage} onPageSizeChange={p => { setPageSize(p); setPage(0); }} pageSizes={PAGE_SIZES} />

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Purchase Details"
        footer={<BtnSecondary onClick={() => setSelected(null)}>Close</BtnSecondary>}>
        {selected && <>
          <DetailRow label="Product" value={formatProductName(selected)} />
          <DetailRow label="Order Price" value={`K ${selected.order_price}`} />
          <DetailRow label="Quantity" value={selected.quantity} />
          <DetailRow label="Unit" value={selected.unit_type} />
          <DetailRow label="Discount" value={selected.discount>0?`K ${selected.discount}`:'None'} />
          <DetailRow label="Date" value={formatDate(selected.purchase_date)} />
          {selected.supplier_name && <DetailRow label="Supplier" value={selected.supplier_name} />}
        </>}
      </Modal>

      <DeleteConfirmModal open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={handleDelete}
        itemDescription={toDelete ? `${formatProductName(toDelete)} · ${formatDate(toDelete.purchase_date)}` : ''} />

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
